import React from 'react';
import { ScreenProps } from '../App';
import { formatCurrency } from '../utils/format';
import { POT_COLOURS } from '../constants/potColours';

const fmt = formatCurrency;

const GROWTH_RATE = 0.08;
const MONTHLY_RATE = GROWTH_RATE / 12;

const BabySteps: React.FC<ScreenProps> = ({ data }) => {
  // Brief 47 S4 — Use total cash savings (EF accounts + savings accounts) to align with Savings lever
  const efAccountsBalance = (data.efAccount1Balance || 0) + (data.efAccount2Balance || 0);
  const otherSavingsBalance = (data.savingsAccounts || []).reduce((s, a) => s + (a.balance || 0), 0);
  const efBalance = efAccountsBalance + otherSavingsBalance;
  const essentials = (data.mortgagePayment || 0) + (data.livingCosts || 0);
  const efMonths = essentials > 0 ? efBalance / essentials : 0;
  const ef3Target = essentials * 3;
  const ef6Target = essentials * 6;
  const investedMonthly = data.isaContribution + data.pensionMonthly + (data.pensionCompanyFunded || 0);
  const otherIncome = (data.payeIncome || 0) + (data.rentalIncomeNet || 0) + (data.savingsInterest || 0) + (data.otherDividends || 0);
  const grossMonthly = (data.salary + data.dividends + otherIncome) / 12;
  const investedPct = grossMonthly > 0 ? (investedMonthly / grossMonthly) * 100 : 0;
  const hasNonMortgageDebt = false; // will be from a debt screen later

  // DLA overdrawn — populated by /api/xero/balance in S2. Only render the
  // extra liability row when the balance is strictly less than 0.
  const dlaBalance = data.businessDlaBalance;
  const dlaOverdrawn = typeof dlaBalance === 'number' && dlaBalance < 0;
  const dlaOwed = dlaOverdrawn ? Math.abs(dlaBalance as number) : 0;

  // FFM monthly target — monthly amount needed to reach Freedom Number
  const freedomAge = data.targetFreedomAge || 60;
  const age = data.clientAge || 40;
  const years = Math.max(0, freedomAge - age);
  const target = data.freedomNumberInflated || 0;
  const currentInvested = (data.pensionBalance || 0) + (data.investmentsBalance || 0);
  const fvCurrentPot = currentInvested * Math.pow(1 + GROWTH_RATE, years);
  const n = years * 12;
  const annuityFactor = MONTHLY_RATE > 0
    ? (Math.pow(1 + MONTHLY_RATE, n) - 1) / MONTHLY_RATE
    : n;
  const ffmMonthlyTarget = target > 0 && annuityFactor > 0
    ? Math.max(0, (target - fvCurrentPot) / annuityFactor)
    : 0;

  const steps = [
    {
      number: 1,
      title: '£1,000 starter emergency fund',
      subtitle: 'Minimum cash buffer',
      status: efBalance >= 1000 ? 'complete' : efBalance > 0 ? 'inprogress' : 'notstarted',
      detail: efBalance >= 1000 ? `${fmt(efBalance)} in savings — step 1 cleared` : `${fmt(efBalance)} saved — need ${fmt(1000 - efBalance)} more`,
    },
    {
      number: 2,
      title: 'Pay off all non-mortgage debt',
      subtitle: 'Debt snowball — smallest to largest',
      status: hasNonMortgageDebt ? 'inprogress' : 'complete',
      detail: 'No consumer debt identified. Director\'s loan is money the business owes you — not a personal liability.',
    },
    {
      number: 3,
      title: '3–6 months fully funded emergency fund',
      subtitle: 'Easy-access savings accounts',
      status: efMonths >= 6 ? 'complete' : 'inprogress',
      detail: essentials > 0
        ? efMonths >= 6
          ? `${fmt(efBalance)} = ${efMonths.toFixed(1)} months. 6-month target: ${fmt(ef6Target)}. Step 3 cleared.`
          : efMonths >= 3
            ? `${fmt(efBalance)} = ${efMonths.toFixed(1)} months. ${(6 - efMonths).toFixed(1)} months to 6-month target (${fmt(ef6Target)}).`
            : `${fmt(efBalance)} = ${efMonths.toFixed(1)} months. ${(3 - efMonths).toFixed(1)} months to 3-month target (${fmt(ef3Target)}).`
        : 'Enter mortgage and living costs in the waterfall screen to calculate.',
      overfundedNote: efMonths > 6 && essentials > 0
        ? `${fmt(Math.round(efBalance - ef6Target))} above your 6-month target — redirect conversation below.`
        : '',
      progress: efMonths > 0 ? Math.min(efMonths / 6, 1) * 100 : 0,
      progressLabel: `${efMonths.toFixed(1)} of 6 months`,
    },
    {
      number: 4,
      title: 'Invest towards your Freedom Number',
      subtitle: 'Pension + investments combined',
      status: ffmMonthlyTarget > 0 && investedMonthly >= ffmMonthlyTarget ? 'complete' : investedMonthly > 0 ? 'inprogress' : 'notstarted',
      detail: ffmMonthlyTarget > 0
        ? `${fmt(investedMonthly)}/month = ${investedPct.toFixed(1)}% of gross. Target: ${fmt(Math.round(ffmMonthlyTarget))}/month — your FFM target.`
        : grossMonthly > 0
        ? `${fmt(investedMonthly)}/month = ${investedPct.toFixed(1)}% of gross. Complete the Freedom Number screen to see your target.`
        : 'Enter income details to calculate.',
      progress: ffmMonthlyTarget > 0 ? Math.min(investedMonthly / ffmMonthlyTarget, 1) * 100 : 0,
      progressLabel: ffmMonthlyTarget > 0 ? `${fmt(investedMonthly)} of ${fmt(Math.round(ffmMonthlyTarget))}/month target` : '',
      directorNote: 'Steps 4 and 6 run simultaneously for ltd company directors — pension CT efficiency makes parallel running correct.',
    },
    {
      number: 5,
      title: 'Save for children\'s education',
      subtitle: 'Junior ISA / university fund',
      status: 'na',
      detail: 'Ramsey\'s step 5 is US college fund focused. UK equivalent is Junior ISA. Mark as N/A if not applicable.',
    },
    {
      number: 6,
      title: 'Pay off home early',
      subtitle: 'Mortgage overpayment strategy',
      status: data.mortgageOP > 0 ? 'inprogress' : 'notstarted',
      detail: data.mortgageOP > 0
        ? `${fmt(data.mortgageOP)}/month overpayment running. Running alongside step 4 — correct for directors.`
        : 'No overpayment set up yet. Add in the waterfall screen.',
    },
    {
      number: 7,
      title: 'Build wealth and give',
      subtitle: 'Invest broadly, build legacy',
      status: data.pensionBalance + data.isaBalance > 10000 ? 'inprogress' : 'notstarted',
      detail: data.pensionBalance + data.isaBalance > 0
        ? `${fmt(data.pensionBalance + data.isaBalance)} already invested (pension + investments). Wealth building underway.`
        : 'Enter pension and ISA balances in the investments screen.',
    },
  ];

  const statusConfig: Record<string, { bg: string; pill: string; pillText: string; dot: string }> = {
    complete: { bg: 'bg-green-50 border-green-200', pill: 'bg-green-100 text-green-700', pillText: '✓ Complete', dot: 'bg-green-500' },
    inprogress: { bg: 'bg-blue-50 border-blue-200', pill: 'bg-blue-100 text-blue-700', pillText: '● In progress', dot: 'bg-blue-500' },
    notstarted: { bg: 'bg-gray-50 border-gray-200', pill: 'bg-gray-100 text-gray-500', pillText: '○ Not started', dot: 'bg-gray-300' },
    na: { bg: 'bg-gray-50 border-gray-100', pill: 'bg-gray-100 text-gray-400', pillText: '— N/A', dot: 'bg-gray-200' },
  };

  return (
    <div className="flex h-full">
      <div className="flex-1 p-8 overflow-y-auto bg-gray-50">
        <div className="max-w-2xl mx-auto">
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Emergency fund</div>
              <div className="text-xl font-semibold text-gray-800">{efMonths.toFixed(1)} months</div>
              <div className="text-xs text-gray-400">{fmt(efBalance)} total</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Invested rate</div>
              <div className={`text-xl font-semibold ${investedPct >= 15 ? 'text-green-600' : 'text-gray-800'}`}>{investedPct.toFixed(1)}%</div>
              <div className="text-xs text-gray-400">of gross income</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Total invested</div>
              <div className="text-xl font-semibold text-gray-800">{fmt(data.pensionBalance + data.isaBalance)}</div>
              <div className="text-xs text-gray-400">pension + investments</div>
            </div>
          </div>

          <div className="space-y-3">
            {dlaOverdrawn && (
              <div
                className="rounded-xl border p-4"
                style={{ backgroundColor: `${POT_COLOURS.debt}15`, borderColor: `${POT_COLOURS.debt}50` }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5 text-white"
                      style={{ backgroundColor: POT_COLOURS.debt }}
                    >
                      !
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800 text-sm">Director's Loan Account (overdrawn)</div>
                      <div className="text-xs text-gray-500 mt-0.5">{fmt(dlaOwed)} owed to the company</div>
                      <div className="text-xs text-gray-500 mt-1.5">
                        Repay to company — separate plan agreed in Session 2
                      </div>
                    </div>
                  </div>
                  <span
                    className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                    style={{ backgroundColor: `${POT_COLOURS.debt}25`, color: POT_COLOURS.debt }}
                  >
                    Liability
                  </span>
                </div>
              </div>
            )}
            {steps.map(step => {
              const cfg = statusConfig[step.status];
              return (
                <div key={step.number} className={`rounded-xl border p-4 ${cfg.bg}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-0.5 ${step.status === 'complete' ? 'bg-green-100 text-green-700' : step.status === 'inprogress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                        {step.number}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 text-sm">{step.title}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{step.subtitle}</div>
                        <div className="text-xs text-gray-500 mt-1.5">{step.detail}</div>
                        {step.directorNote && (
                          <div className="text-xs text-purple-600 mt-1.5 bg-purple-50 rounded px-2 py-1">{step.directorNote}</div>
                        )}
                        {(step as any).overfundedNote && (
                          <div className="text-xs text-amber-700 mt-1.5 bg-amber-50 rounded px-2 py-1">{(step as any).overfundedNote}</div>
                        )}
                        {(step as any).progress !== undefined && (step as any).progress > 0 && (
                          <div className="mt-2">
                            <div className="h-1.5 bg-white rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: (step as any).progress + '%' }} />
                            </div>
                            <div className="text-xs text-gray-400 mt-1">{(step as any).progressLabel}</div>
                          </div>
                        )}
                      </div>
                    </div>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ${cfg.pill}`}>{cfg.pillText}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 bg-ma-navy rounded-xl p-4 text-sm text-blue-100">
            <span className="font-medium text-white">Director note: </span>
            Steps 4 and 6 run simultaneously — CT relief on employer pension makes parallel running financially correct. Step 3 target should be 6 months for variable income directors — best practice is to hold 3 of those 6 months in company reserves (not personally) for tax efficiency.
          </div>
        </div>
      </div>
    </div>
  );
};

export default BabySteps;
